import { Collapse, Space, Switch, Typography } from "antd";
import { APP_CATALOG, APP_KINDS } from "@/common/app-catalog";
import {
	useEnabledAppKinds,
	useUpdateWorkspaceApp,
	useWorkspaceApps,
} from "@/common/artemis-operations/apps";
import { useGitProviderConnection } from "@/common/artemis-operations/workspaces";
import { type AppKind, AppStatus } from "@/common/model";
import { useNotify } from "@/common/use-notify";
import { BaseContainer, QueryGuard } from "@/components/base";

/**
 * The apps the organization admin has made available (ADR-061), one row each with the workspace
 * enable switch. GitHub is connected once for the organization (ADR-062); its row only says which
 * account the workspace will use once enabled.
 */
export function WorkspaceAppsSection({ workspaceId }: { workspaceId: string }) {
	const notify = useNotify();
	const enabledKinds = useEnabledAppKinds();
	const workspaceApps = useWorkspaceApps(workspaceId);
	const updateApp = useUpdateWorkspaceApp(workspaceId);
	const { data: connection } = useGitProviderConnection();

	const kinds = APP_KINDS.filter((k) => enabledKinds.data?.includes(k));
	const statusOf = (kind: AppKind) =>
		workspaceApps.data?.find((a) => a.kind === kind)?.status ===
		AppStatus.ENABLED;

	function handleToggle(kind: AppKind, checked: boolean) {
		updateApp.mutate(
			{ kind, status: checked ? AppStatus.ENABLED : AppStatus.DISABLED },
			{
				onSuccess: () =>
					notify.success(
						`${APP_CATALOG[kind].name} ${checked ? "enabled" : "disabled"} in this workspace.`,
					),
				onError: (err) =>
					notify.error((err as Error).message || "Failed to update the app."),
			},
		);
	}

	function body(kind: AppKind) {
		if (kind === "GIT_HUB") {
			return (
				<Typography.Text>
					{connection
						? `Connected to ${connection.accountLogin || connection.installationId}. Enable it here to browse and link its repositories from this workspace's projects.`
						: "Not connected yet. An organization admin connects GitHub under Admin → Apps."}
				</Typography.Text>
			);
		}
		return <Typography.Text>{APP_CATALOG[kind].description}</Typography.Text>;
	}

	return (
		<BaseContainer title="Apps">
			<QueryGuard
				isPending={enabledKinds.isPending || workspaceApps.isPending}
				isError={enabledKinds.isError || workspaceApps.isError}
				isNull={false}
			>
				{kinds.length === 0 ? (
					<Typography.Text>
						No app is enabled for your organization yet.
					</Typography.Text>
				) : (
					<Collapse
						style={{ width: "100%" }}
						items={kinds.map((kind) => ({
							key: kind,
							label: APP_CATALOG[kind].name,
							extra: (
								// antd requires an explicit stopPropagation here, else clicking
								// `extra` also toggles the panel.
								<Space onClick={(e) => e.stopPropagation()}>
									<Switch
										size="small"
										checked={statusOf(kind)}
										loading={updateApp.isPending}
										onChange={(checked) => handleToggle(kind, checked)}
										aria-label={`Enable ${APP_CATALOG[kind].name} in this workspace`}
									/>
								</Space>
							),
							children: body(kind),
						}))}
					/>
				)}
			</QueryGuard>
		</BaseContainer>
	);
}
